import { Component, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { concatMap, take, tap } from 'rxjs/operators';

import { RfpComponent } from 'app/offer/rfp/rfp.component';
import { htmlEscapeDecode } from 'app/utils/escape-html';
import {
  AppCustomPropertyInputArbonia as CustomPropertyInputArbonia,
  RevisionsStatus,
  QuoteCustomPropertyKeyType as CustomPropKey,
  Offer
} from 'graphql/models/extended';
import { Account, Branch } from 'graphql/models/generated';
import { DropdownItem } from 'models/dropdown.model';
import { SendOfferEmailSettings } from 'models/offer-send-mail.model';
import { OfferBase } from 'models/offer.model';
import { AccountService } from 'shared/services/account.service';
import { OfferService } from 'shared/services/offer.service';
import { StoreService } from 'shared/services/store.service';
import { PricingService } from 'shared/services/pricing.service'

@Component({
  selector: 'app-rfp-modal',
  templateUrl: './rfp-modal.component.html',
  styleUrls: ['./rfp-modal.component.scss']
})
export class RfpModalComponent {
  @ViewChild(RfpComponent) rfpComponent!: RfpComponent;

  get form(): UntypedFormGroup {
    return this.rfpComponent.form;
  }

  get dropdownManufacturesList(): DropdownItem[] {
    return this.rfpComponent.dropdownData.manufacturers;
  }

  constructor(
    private accountService: AccountService,
    private offerService: OfferService,
    private pricingService: PricingService,
    private storeService: StoreService,
    private translateService: TranslateService
  ) { }

  onSubmit(): Observable<any> {
    const subject = this.translateService.instant(this.form.controls.subject.value);
    const email = this.form.controls.email.value as string;
    const recipientData = this.dropdownManufacturesList.find((m) => m.value === email)
    const recipient = (recipientData && recipientData.title ? `"${recipientData.title}" <${email}>` : email);
    const textString = this.form.controls.message.value as string;
    const text = htmlEscapeDecode(textString);

    return combineLatest([
      this.storeService.isCurrentAccountDealer(),
      this.storeService.getCurrentOffer(),
      this.storeService.getCurrentAccountBranch(),
      this.accountService.getAccountBasic(email)
    ]).pipe(
      concatMap(([isDealer, offer, branch, recipientAccount]: [boolean, OfferBase, Branch, Account]) => {
        const offerDocumentId = { documentId: offer.documentId, revision: offer.revision };

        const mailSubject = `${subject} - ${branch.name}`;
        const offerLink = window.location.href;
        const offerLinkText = this.translateService.instant('OFFER.EMAIL.GO_TO_OFFER', { offerLink });
        const mailText = `${text}\n\n${offerLinkText}`;

        const mailSettings: SendOfferEmailSettings = {
          attachPdfPreview: false,
          bccToCurrentUser: true,
          subject: mailSubject,
          recipient: recipient,
          text: mailText,
          sendAdditionalEmail: false,
          offerLink,
          offerLinkText,
          title: this.offerService.getCurrentOfferTitle(offer as Offer, isDealer)
        };

        const sendOffer$ = this.offerService.sendOfferEmail(offerDocumentId, mailSettings);

        const customProperties: CustomPropertyInputArbonia[] = [
          { key: CustomPropKey.SendSubject, value: subject, propertyType: 'String' },
          { key: CustomPropKey.SendUserId, value: (recipientAccount && recipientAccount.id) || '', propertyType: 'String' },
          { key: CustomPropKey.SendMessage, value: text, propertyType: 'String' }
        ];

        return sendOffer$.pipe(
          concatMap(() => this.pricingService.setOfferAutoUpdatePrices(offer as Offer, true)),
          concatMap((offer) => this.offerService.setOfferStatus(offer, RevisionsStatus.PENDING_RFP)),
          concatMap(() => this.offerService.updateOffer(offer, customProperties)),
          concatMap(() => this.offerService.updateOfferTitle(
            recipientAccount.id,
            { documentId: offer.documentId, revision: offer.revision }
          ).pipe(
            tap((response) => {
              this.storeService.setOfferUpdate({
                dealerOfferTitle: response.data.updateOfferTitle.dealerTitle,
                manufacturerOfferTitle: response.data.updateOfferTitle.manufacturerTitle,
              })
            })
          )),
          concatMap(() => this.offerService.lockOffer(offer))
        );
      }),
      take(1)
    );
  }
}
